import type { MessageFormatElement } from "react-intl";

import { englishMessages } from "@/i18n/catalogs/en";

export const DEFAULT_LOCALE = "en";
export const PSEUDO_LOCALE = "en-XA";
export const LOCALE_STORAGE_KEY = "celsus.ui-locale";
export const supportedLocales = [
  DEFAULT_LOCALE,
  "es",
  "de",
  "fr",
  PSEUDO_LOCALE,
] as const;
export type SupportedLocale = (typeof supportedLocales)[number];
export type MessageCatalog =
  Record<string, string> | Record<string, MessageFormatElement[]>;

const catalogLoaders: Record<SupportedLocale, () => Promise<MessageCatalog>> = {
  en: async () => englishMessages,
  es: async () => {
    const { spanishMessages } = await import("@/i18n/catalogs/es");
    return spanishMessages;
  },
  de: async () => {
    const { germanMessages } = await import("@/i18n/catalogs/de");
    return germanMessages;
  },
  fr: async () => {
    const { frenchDraftMessages } = await import("@/i18n/catalogs/fr");
    return frenchDraftMessages;
  },
  "en-XA": async () => {
    const { pseudolocalizeCatalog } = await import("@/i18n/pseudolocalize");
    return pseudolocalizeCatalog(englishMessages);
  },
};

export function isSupportedLocale(
  value: string | null | undefined
): value is SupportedLocale {
  return supportedLocales.some((locale) => locale === value);
}

export function detectLocale(
  storedLocale: string | null = window.localStorage.getItem(LOCALE_STORAGE_KEY),
  browserLocales: readonly string[] = window.navigator.languages
): SupportedLocale {
  if (isSupportedLocale(storedLocale)) return storedLocale;
  for (const locale of browserLocales) {
    if (isSupportedLocale(locale)) return locale;
    const baseLocale = locale.split("-")[0];
    if (isSupportedLocale(baseLocale)) return baseLocale;
  }
  return DEFAULT_LOCALE;
}

export async function loadCatalog(locale: SupportedLocale): Promise<MessageCatalog> {
  return catalogLoaders[locale]();
}
